import { supabase } from '@/lib/supabase';

export interface Device {
  id: string;
  name: string;
  model: string;
  price: number;
  description: string;
  image_url: string;
  is_available: boolean;
  warranty_months: number;
  created_at?: string;
}

export interface UserDevice {
  id: string;
  userId: string;
  deviceId: string;
  orderId: string;
  serialNumber: string;
  purchaseDate: string;
  warrantyExpiry: string;
  status: 'active' | 'inactive' | 'warranty_expired';
  notes?: string;
  createdAt: string;
  updatedAt: string;
  device?: Device;
}

/**
 * Fetch all available devices from the database
 */
async function getAllDevices(): Promise<Device[]> {
  try {
    const { data, error } = await supabase
      .from('devices')
      .select('*')
      .eq('is_available', true)
      .order('price', { ascending: true });

    if (error) {
      throw error;
    }

    return data || [];
  } catch (error: any) {
    if (error?.message?.includes('Failed to fetch') || 
        error?.message?.includes('NetworkError') ||
        error?.name === 'TypeError' && error?.message?.includes('fetch')) {
      throw new Error('Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.');
    }
    throw error;
  }
}

/**
 * Fetch a single device by ID
 */
async function getDeviceById(deviceId: string): Promise<Device | null> {
  try {
    const { data, error } = await supabase
      .from('devices')
      .select('*')
      .eq('id', deviceId)
      .single();

    if (error) {
      throw error;
    }

    return data;
  } catch (error: any) {
    if (error?.message?.includes('Failed to fetch') || 
        error?.message?.includes('NetworkError') ||
        error?.name === 'TypeError' && error?.message?.includes('fetch')) {
      throw new Error('Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.');
    }
    throw error;
  }
}

/**
 * Search devices by name or model
 */
async function searchDevices(query: string): Promise<Device[]> {
  try {
    const { data, error } = await supabase
      .from('devices')
      .select('*')
      .or(`name.ilike.%${query}%,model.ilike.%${query}%`)
      .eq('is_available', true)
      .order('price', { ascending: true });

    if (error) {
      throw error;
    }

    return data || [];
  } catch (error: any) {
    if (error?.message?.includes('Failed to fetch') || 
        error?.message?.includes('NetworkError') ||
        error?.name === 'TypeError' && error?.message?.includes('fetch')) {
      throw new Error('Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.');
    }
    throw error;
  }
}

/**
 * Get devices within a price range
 */
async function getDevicesByPriceRange(
  minPrice: number,
  maxPrice: number
): Promise<Device[]> {
  try {
    const { data, error } = await supabase
      .from('devices')
      .select('*')
      .gte('price', minPrice)
      .lte('price', maxPrice)
      .eq('is_available', true)
      .order('price', { ascending: true });

    if (error) {
      throw error;
    }

    return data || [];
  } catch (error: any) {
    if (error?.message?.includes('Failed to fetch') || 
        error?.message?.includes('NetworkError') ||
        error?.name === 'TypeError' && error?.message?.includes('fetch')) {
      throw new Error('Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.');
    }
    throw error;
  }
}

/**
 * Get user's registered devices
 */
async function getUserDevices(userId: string): Promise<UserDevice[]> {
  try {
    const { data, error } = await supabase
      .from('user_devices')
      .select(`
        *,
        devices (*)
      `)
      .eq('user_id', userId)
      .order('purchase_date', { ascending: false });

    if (error) {
      throw error;
    }

    return (data || []).map((row: any) => ({
      id: row.id,
      userId: row.user_id,
      deviceId: row.device_id,
      orderId: row.order_id,
      serialNumber: row.serial_number,
      purchaseDate: row.purchase_date,
      warrantyExpiry: row.warranty_expiry,
      status: row.status,
      notes: row.notes,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
      device: row.devices ? {
        id: row.devices.id,
        name: row.devices.name,
        model: row.devices.model,
        description: row.devices.description,
        price: row.devices.price,
        image_url: row.devices.image_url,
        is_available: row.devices.is_available,
        warranty_months: row.devices.warranty_months,
        created_at: row.devices.created_at,
      } : undefined,
    }));
  } catch (error: any) {
    if (error?.message?.includes('Failed to fetch') || 
        error?.message?.includes('NetworkError') ||
        error?.name === 'TypeError' && error?.message?.includes('fetch')) {
      throw new Error('Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.');
    }
    throw error;
  }
}

// Export as a service object
export const deviceService = {
  getAllDevices,
  getDeviceById,
  searchDevices,
  getDevicesByPriceRange,
  getUserDevices
};